<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1">
    <!-- Header -->
    <v-row align="center" class="mb-0 px-0" style="min-height: 48px;">
      <v-col cols="auto" class="d-flex align-center ga-2">
        <v-btn color="primary" variant="outlined" prepend-icon="mdi-plus" @click="showAddDialog = true">
          Добавить сайт
        </v-btn>
      </v-col>
      <v-col class="text-center">
        <h4 class="mb-0">Сайты — источники</h4>
      </v-col>
      <v-col cols="auto">
        <div class="d-flex align-center ga-2">
          <v-text-field
            v-model="filters.identifier"
            placeholder="URL / название"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            prepend-inner-icon="mdi-magnify"
            style="width: 260px;"
            @update:model-value="onFilterDebounce"
          />
          <v-select
            v-model="filters.is_monitoring"
            :items="monitoringOptions"
            item-title="text"
            item-value="value"
            placeholder="Мониторинг"
            variant="outlined"
            density="compact"
            hide-details
            clearable
            style="width: 160px;"
            @update:model-value="loadSources"
          />
          <span class="text-caption font-weight-bold">Найдено: {{ total }}</span>
        </div>
      </v-col>
    </v-row>

    <v-card>
      <v-card-text class="pa-0">
        <v-data-table-server
          :headers="headers"
          :items="sources"
          :items-length="total"
          :loading="isLoading"
          :items-per-page="itemsPerPage"
          :page="page"
          item-value="id"
          hover
          fixed-header
          height="calc(100vh - 236px)"
          :items-per-page-options="[20, 50, 100]"
          @update:options="onTableOptions"
        >
          <!-- URL -->
          <template #[`item.identifier`]="{ item }">
            <a
              :href="cleanUrl(item.identifier)"
              target="_blank"
              rel="noopener"
              class="text-primary text-decoration-none text-truncate d-inline-block"
              style="max-width: 320px;"
              :title="cleanUrl(item.identifier)"
            >
              {{ cleanUrl(item.identifier) }}
            </a>
          </template>

          <!-- Название -->
          <template #[`item.display_name`]="{ item }">
            {{ displayLabel(item) }}
          </template>

          <!-- Метод discovery (как доп. колонка статуса) -->
          <template #[`item.discovery_method`]="{ item }">
            <v-chip v-if="discoveryLabel(item)" size="x-small" variant="tonal">
              {{ discoveryLabel(item) }}
            </v-chip>
          </template>

          <!-- Статус -->
          <template #[`item.last_extract_status`]="{ item }">
            <v-tooltip v-if="siteHealth(item)" :text="siteHealth(item).tooltip" location="top">
              <template #activator="{ props }">
                <v-chip v-bind="props" :color="siteHealth(item).color" size="small" variant="tonal">
                  {{ siteHealth(item).label }}
                </v-chip>
              </template>
            </v-tooltip>
            <template v-else>
            <v-chip
              :color="statusColor(extra(item).last_extract_status)"
              size="small"
              variant="tonal"
            >
              {{ statusLabel(extra(item).last_extract_status) }}
            </v-chip>
            </template>
          </template>

          <!-- Обновлено -->
          <template #[`item.last_check_at`]="{ item }">
            <span class="text-caption">{{ formatDateTime(extra(item).last_check_at) }}</span>
          </template>

          <!-- Статей (live count из ES, ссылка → модалка) -->
          <template #[`item.messages_count`]="{ item }">
            <div
              class="text-right"
              :class="{ 'text-primary cursor-pointer': getMessagesCount(item) > 0 }"
              @click="getMessagesCount(item) > 0 && openMessagesModal(item)"
            >
              {{ formatNumber(getMessagesCount(item)) }}
            </div>
          </template>

          <!-- Действия -->
          <template #[`item.actions`]="{ item }">
            <div class="d-flex justify-center">
              <v-tooltip v-if="item.is_monitoring" text="Остановить">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-stop"
                    size="x-small"
                    variant="text"
                    color="warning"
                    @click="stopMonitoring(item)"
                  />
                </template>
              </v-tooltip>
              <v-tooltip v-else text="Запустить">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-play"
                    size="x-small"
                    variant="text"
                    color="success"
                    :disabled="item.is_processing"
                    @click="startMonitoring(item)"
                  />
                </template>
              </v-tooltip>
              <v-tooltip text="Редактировать">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-pencil"
                    size="x-small"
                    variant="text"
                    color="primary"
                    @click="openEditModal(item)"
                  />
                </template>
              </v-tooltip>
              <v-tooltip text="Удалить">
                <template #activator="{ props }">
                  <v-btn
                    v-bind="props"
                    icon="mdi-delete"
                    size="x-small"
                    variant="text"
                    color="error"
                    :disabled="item.is_monitoring"
                    @click="confirmDelete(item)"
                  />
                </template>
              </v-tooltip>
            </div>
          </template>

          <template #no-data>
            <div class="text-center pa-8">
              <v-icon size="48" color="grey-lighten-1" class="mb-3">mdi-web</v-icon>
              <div class="text-body-1 text-grey">Пока нет веб-источников</div>
              <div class="text-caption text-grey">
                Нажмите «Добавить сайт» в шапке, чтобы подключить новый.
              </div>
            </div>
          </template>
        </v-data-table-server>
      </v-card-text>
    </v-card>

    <AddWebSourceDialog
      v-model="showAddDialog"
      :edit-source="webEditSource"
      @created="onSourceCreated"
      @updated="onWebSourceUpdated"
      @update:model-value="v => { if (!v) webEditSource = null; }"
    />

    <!-- Edit modal -->
    <v-dialog v-model="editModal.visible" max-width="640" persistent @keydown.esc="closeEditModal">
      <v-card v-if="editModal.item">
        <v-card-title class="d-flex align-center ga-2">
          <v-icon icon="mdi-web" />
          <span>Редактировать сайт</span>
        </v-card-title>
        <v-card-text>
          <v-alert
            v-if="editModal.error"
            type="error"
            variant="tonal"
            density="compact"
            class="mb-3"
            closable
            @click:close="editModal.error = ''"
          >
            {{ editModal.error }}
          </v-alert>

          <v-text-field
            :model-value="cleanUrl(editModal.item.identifier)"
            label="URL"
            variant="outlined"
            density="compact"
            readonly
            append-inner-icon="mdi-open-in-new"
            @click:append-inner="openUrl(cleanUrl(editModal.item.identifier))"
            class="mb-2"
          />

          <v-text-field
            v-model="editForm.display_name"
            label="Название"
            variant="outlined"
            density="compact"
            :placeholder="hostnameOf(cleanUrl(editModal.item.identifier))"
            persistent-placeholder
            class="mb-2"
          />

          <v-row dense class="mt-0 mb-1">
            <v-col cols="6">
              <div class="text-caption text-medium-emphasis">Метод</div>
              <v-chip
                v-if="discoveryLabel(editModal.item)"
                size="small"
                variant="tonal"
              >
                {{ discoveryLabel(editModal.item) }}
              </v-chip>
              <span v-else class="text-body-2">—</span>
            </v-col>
            <v-col cols="6">
              <div class="text-caption text-medium-emphasis">Последний статус</div>
              <v-chip
                :color="statusColor(extra(editModal.item).last_extract_status)"
                size="small"
                variant="tonal"
              >
                {{ statusLabel(extra(editModal.item).last_extract_status) }}
              </v-chip>
            </v-col>
          </v-row>
          <div class="text-caption text-medium-emphasis mb-3">
            Последняя проверка: {{ formatDateTime(extra(editModal.item).last_check_at) }}
          </div>

          <v-combobox
            v-model="editForm.url_patterns"
            label="Паттерны URL статей"
            hint="Glob: /news/*, /resource/**, ..."
            persistent-hint
            multiple
            chips
            closable-chips
            clearable
            hide-no-data
            variant="outlined"
            density="compact"
            class="mt-2 mb-4"
          />

          <v-combobox
            v-model="editForm.block_selectors"
            label="CSS-селекторы блоков (опционально)"
            hint="Селекторы, найденные автоподбором или заданные пользователем (site-state)."
            persistent-hint
            multiple
            chips
            closable-chips
            clearable
            hide-no-data
            variant="outlined"
            density="compact"
            class="mt-2 mb-4"
          />

          <v-text-field
            v-model="editForm.feed_url"
            label="Feed URL"
            placeholder="https://example.com/rss"
            variant="outlined"
            density="compact"
            class="mt-2 mb-2"
          />

          <v-checkbox
            v-model="editForm.requires_js"
            label="Требует JavaScript-рендеринг (само-определяется)"
            density="compact"
            hint="Воркер определяет это автоматически (self-learning) — ручное значение может быть перезаписано. Правьте только для отладки."
            persistent-hint
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="closeEditModal" :disabled="editModal.saving">Отмена</v-btn>
          <v-btn
            color="primary"
            variant="elevated"
            :loading="editModal.saving"
            @click="saveEdit"
          >
            Сохранить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete confirm -->
    <v-dialog v-model="deleteModal.visible" max-width="460" @keydown.esc="deleteModal.visible = false">
      <v-card v-if="deleteModal.item">
        <v-card-title>Удаление сайта</v-card-title>
        <v-card-text>
          <div class="mb-2">
            Удалить <strong>{{ deleteModal.item.display_name || cleanUrl(deleteModal.item.identifier) }}</strong>?
          </div>
          <div class="text-caption text-medium-emphasis">
            Вместе с источником будут удалены все связанные статьи в Elasticsearch
            и project findings. Действие необратимо.
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn variant="text" @click="deleteModal.visible = false" :disabled="deleteModal.deleting">Отмена</v-btn>
          <v-btn
            color="error"
            variant="elevated"
            :loading="deleteModal.deleting"
            @click="doDelete"
          >
            Удалить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <SourceContentModal
      v-model="messagesModal.visible"
      :source-id="messagesModal.sourceId"
      :title="messagesModal.title"
      items-label="статей"
    />

    <v-snackbar
      v-model="snackbar.visible"
      :color="snackbar.color"
      location="bottom right"
      timeout="3000"
    >
      {{ snackbar.text }}
    </v-snackbar>
  </v-container>
</template>

<script>
import apiClient from '@/apiClient';
import apiDataClient from '@/apiDataClient';
import { decodeHostname } from '@/utils/punycode';
import SourceContentModal from '@/components/SourceContentModal.vue';
import AddWebSourceDialog from '@/components/projects/form/AddWebSourceDialog.vue';

const DEFAULT_ITEMS_PER_PAGE = 50;

// Sortable columns -> sort fields of GET /v2/platform-sources (core platform_source_repository
// sort_column)
const WEBSITE_SORT_FIELDS = {
  display_name: 'display_name',
  identifier: 'identifier',
  discovery_method: 'method',
  last_extract_status: 'last_extract_status',
  last_check_at: 'last_check_at',
  // stored by data after it saves articles (sources_platform.messages_count)
  messages_count: 'messages_count',
};

export default {
  name: 'Websites',
  components: { SourceContentModal, AddWebSourceDialog },
  data() {
    return {
      sources: [],
      total: 0,
      isLoading: false,
      platformId: null,
      page: 1,
      itemsPerPage: DEFAULT_ITEMS_PER_PAGE,
      showAddDialog: false,
      filters: {
        identifier: '',
        is_monitoring: null,
      },
      filterTimeout: null,
      monitoringOptions: [
        { text: 'Все', value: null },
        { text: 'Активные', value: true },
        { text: 'Выключенные', value: false },
      ],
      headers: [
        { title: 'Название', key: 'display_name', width: '220px' },
        { title: 'URL', key: 'identifier', minWidth: '260px' },
        { title: 'Метод', key: 'discovery_method', width: '120px' },
        { title: 'Статус', key: 'last_extract_status', width: '120px' },
        { title: 'Обновлено', key: 'last_check_at', width: '160px' },
        { title: 'Статей', key: 'messages_count', align: 'end', width: '110px' },
        { title: 'Действия', key: 'actions', sortable: false, width: '160px', align: 'center' },
      ],
      sortBy: null,  // {key, order} of the table, sent as sort_by / sort_order
      messagesCount: {},
      messagesModal: { visible: false, sourceId: null, title: '' },
      editModal: { visible: false, item: null, saving: false, error: '' },
      editForm: this.makeEmptyEditForm(),
      webEditSource: null,
      deleteModal: { visible: false, item: null, deleting: false },
      snackbar: { visible: false, text: '', color: 'success' },
    };
  },
  async mounted() {
    await this.resolvePlatformId();
    this.loadSources();
  },
  methods: {
    makeEmptyEditForm() {
      return {
        display_name: '',
        url_patterns: [],
        block_selectors: [],
        feed_url: '',
        requires_js: false,
      };
    },
    extra(item) {
      return item?.extra_attributes || {};
    },
    hostnameOf(url) {
      if (!url) return '';
      try {
        // Кириллический домен приходит в punycode, показываем «сайт.рф»
        const h = decodeHostname(new URL(url).hostname);
        return h.replace(/^www\./, '');
      } catch {
        return url;
      }
    },
    displayLabel(item) {
      const name = (item?.display_name || '').trim();
      // If display_name is empty OR holds the URL itself (a leftover from
      // an earlier save) — fall back to the hostname so the URL column
      // doesn't get duplicated here.
      if (!name || /^https?:\/\//i.test(name)) {
        return this.hostnameOf(item?.identifier);
      }
      return name;
    },
    discoveryLabel(item) {
      const e = this.extra(item);
      // Пин владельца показываем сразу (не ждём следующего цикла) — это
      // эффективный метод; discovery_method = чем реально собрали в прошлый раз.
      const m = e.pinned_method || e.discovery_method;
      const label = ({
        rss: 'RSS',
        sitemap: 'Sitemap',
        jsonld: 'JSON-LD',
        heuristic: 'Эвристика',
        user_pattern: 'Паттерны',
        llm: 'AI',
        selector: 'Селектор',
        multi: 'Несколько',
      })[m] || m || '';
      return e.pinned_method ? `${label} (закреплён)` : label;
    },
    // Та же 3-состоянческая логика, что webHealth в «Моих источниках»,
    // чтобы админ-таблица и пользовательская показывали одно и то же.
    // Сырой last_extract_status — в тултипе (для отладки).
    siteHealth(item) {
      const e = this.extra(item);
      const st = e.last_extract_status;
      if (!st && !e.last_check_at) return null; // ещё не проверялся — старый чип «—»
      const raw = `last_extract_status: ${st || '—'}`;
      if (st === 'discovery_empty' || st === 'blocked') {
        return {
          color: 'error', label: 'Ошибка сбора',
          tooltip: `Метод получения ссылок не находит статьи. ${raw}`,
        };
      }
      const STALE_DAYS = 7;
      if (e.last_new_at && (Date.now() - new Date(e.last_new_at).getTime()) > STALE_DAYS * 864e5) {
        return {
          color: 'warning', label: 'Нет новых',
          tooltip: `Сбор работает, но новых статей не было ${STALE_DAYS}+ дней. ${raw}`,
        };
      }
      return { color: 'success', label: 'Работает', tooltip: `Источник собирается штатно. ${raw}` };
    },
    statusLabel(s) {
      return ({
        ok: 'OK',
        empty: 'Пусто',
        blocked: 'Заблокирован',
        partial: 'Частично',
        discovery_empty: 'Нет ссылок',
      })[s] || s || '—';
    },
    statusColor(s) {
      return ({
        ok: 'success',
        empty: 'warning',
        partial: 'warning',
        blocked: 'error',
        discovery_empty: 'warning',
      })[s] || 'grey';
    },
    formatDateTime(iso) {
      if (!iso) return '—';
      const d = new Date(iso);
      if (isNaN(d.getTime()) || d.getFullYear() === 1970) return '—';
      const pad = (x) => String(x).padStart(2, '0');
      return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    },
    formatNumber(n) {
      return Number(n || 0).toLocaleString('ru-RU');
    },
    async resolvePlatformId() {
      try {
        const resp = await apiClient.get('/reference/platforms');
        const items = resp.data?.items || [];
        const userWeb = items.find(p => p.name === 'user_web');
        this.platformId = userWeb ? userWeb.id : null;
      } catch (e) {
        console.error('Failed to resolve user_web platform id:', e);
      }
    },
    async loadSources() {
      if (!this.platformId) return;
      this.isLoading = true;
      try {
        const params = {
          platform_id: this.platformId,
          offset: (this.page - 1) * this.itemsPerPage,
          limit: this.itemsPerPage,
        };
        if (this.sortBy && WEBSITE_SORT_FIELDS[this.sortBy.key]) {
          params.sort_by = WEBSITE_SORT_FIELDS[this.sortBy.key];
          params.sort_order = this.sortBy.order === 'desc' ? 'desc' : 'asc';
        }
        if (this.filters.identifier) params.identifier = this.filters.identifier;
        if (this.filters.is_monitoring !== null && this.filters.is_monitoring !== undefined) {
          params.is_monitoring = this.filters.is_monitoring;
        }
        const resp = await apiClient.get('/v2/platform-sources', { params });
        this.sources = resp.data.items || [];
        this.total = resp.data.total || 0;
        this.fetchMessageCounts(this.sources);
      } catch (e) {
        console.error(e);
        this.showSnackbar('Ошибка загрузки источников', 'error');
      } finally {
        this.isLoading = false;
      }
    },
    async fetchMessageCounts(items) {
      const ids = (items || []).map(s => s.id).filter(Boolean);
      if (!ids.length) return;
      try {
        const { data } = await apiDataClient.get('/source-content/count-by-sources', {
          params: { source_ids: ids.join(',') },
        });
        this.messagesCount = { ...this.messagesCount, ...(data.counts || {}) };
      } catch (e) {
        console.error('fetchMessageCounts error:', e);
      }
    },
    getMessagesCount(item) {
      if (!item || !item.id) return 0;
      return this.messagesCount[item.id] || 0;
    },
    openMessagesModal(item) {
      this.messagesModal.sourceId = item.id;
      this.messagesModal.title = item.display_name || this.cleanUrl(item.identifier);
      this.messagesModal.visible = true;
    },
    onTableOptions({ page, itemsPerPage, sortBy }) {
      let changed = false;
      if (page && page !== this.page) { this.page = page; changed = true; }
      if (itemsPerPage && itemsPerPage !== this.itemsPerPage) { this.itemsPerPage = itemsPerPage; changed = true; }
      // the order goes to the server: it sorts the whole selection, not the loaded page
      const sort = sortBy && sortBy.length ? { key: sortBy[0].key, order: sortBy[0].order } : null;
      if (JSON.stringify(sort) !== JSON.stringify(this.sortBy)) {
        this.sortBy = sort;
        if (this.page !== 1) this.page = 1;
        changed = true;
      }
      if (changed) this.loadSources();
    },
    onFilterDebounce() {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => { this.page = 1; this.loadSources(); }, 300);
    },
    async startMonitoring(item) {
      try {
        await apiClient.post(`/v2/platform-sources/${item.id}/start-monitoring`);
        item.is_monitoring = true;
        this.showSnackbar('Мониторинг запущен');
      } catch (e) {
        console.error(e);
        this.showSnackbar(this.extractError(e) || 'Не удалось запустить мониторинг', 'error');
      }
    },
    async stopMonitoring(item) {
      try {
        await apiClient.post(`/v2/platform-sources/${item.id}/stop-monitoring`);
        item.is_monitoring = false;
        this.showSnackbar('Мониторинг остановлен');
      } catch (e) {
        console.error(e);
        this.showSnackbar(this.extractError(e) || 'Не удалось остановить мониторинг', 'error');
      }
    },
    async openEditModal(item) {
      // Per-user isolation: web Source has exactly one owner — open the SAME
      // form the owner uses (per concept). Old modal stays as fallback for
      // legacy sources without a user link.
      try {
        const r = await apiClient.get(`/user-sources/by-source/${item.id}`);
        this.webEditSource = r.data;
        this.showAddDialog = true;
        return;
      } catch (e) { /* 404 → нет привязки, откроем старую модалку */ }
      this.openLegacyEditModal(item);
    },
    onWebSourceUpdated() {
      this.showSnackbar('Сохранено');
      this.webEditSource = null;
      this.loadSources();
    },
    // Отображаемый URL: у per-user веб-источников identifier хранится с
    // суффиксом ::u{user_id} (уникальность в БД); наружу показываем без него.
    cleanUrl(identifier) {
      return (identifier || '').split('::u')[0];
    },
    openLegacyEditModal(item) {
      const e = this.extra(item);
      this.editForm = {
        display_name: item.display_name || '',
        url_patterns: Array.isArray(e.url_patterns) ? [...e.url_patterns] : [],
        block_selectors: Array.isArray(e.block_selectors)
          ? [...e.block_selectors]
          : (e.block_selector ? [e.block_selector] : []),
        feed_url: e.feed_url || '',
        requires_js: !!e.requires_js,
      };
      this.editModal = { visible: true, item, saving: false, error: '' };
    },
    closeEditModal() {
      if (this.editModal.saving) return;
      this.editModal = { visible: false, item: null, saving: false, error: '' };
      this.editForm = this.makeEmptyEditForm();
    },
    async saveEdit() {
      const item = this.editModal.item;
      if (!item) return;
      this.editModal.saving = true;
      this.editModal.error = '';
      try {
        const extra_attributes = {
          url_patterns: this.editForm.url_patterns || [],
          block_selectors: this.editForm.block_selectors || [],
          feed_url: (this.editForm.feed_url || '').trim() || null,
          requires_js: !!this.editForm.requires_js,
        };
        const payload = {
          display_name: (this.editForm.display_name || '').trim() || null,
          extra_attributes,
        };
        await apiClient.put(`/v2/platform-sources/${item.id}`, payload);
        // Reflect into the local row so the UI updates without a reload.
        item.display_name = payload.display_name;
        item.extra_attributes = { ...(item.extra_attributes || {}), ...extra_attributes };
        this.showSnackbar('Сохранено');
        this.closeEditModal();
      } catch (e) {
        console.error(e);
        this.editModal.error = this.extractError(e) || 'Не удалось сохранить';
      } finally {
        this.editModal.saving = false;
      }
    },
    confirmDelete(item) {
      this.deleteModal = { visible: true, item, deleting: false };
    },
    async doDelete() {
      const item = this.deleteModal.item;
      if (!item) return;
      this.deleteModal.deleting = true;
      try {
        await apiClient.delete(`/v2/platform-sources/${item.id}`);
        this.sources = this.sources.filter(s => s.id !== item.id);
        this.total = Math.max(0, this.total - 1);
        this.showSnackbar('Источник удалён');
        this.deleteModal = { visible: false, item: null, deleting: false };
      } catch (e) {
        console.error(e);
        this.showSnackbar(this.extractError(e) || 'Не удалось удалить', 'error');
        this.deleteModal.deleting = false;
      }
    },
    async onSourceCreated() {
      this.showSnackbar('Сайт добавлен');
      await this.loadSources();
    },
    openUrl(url) {
      if (!url) return;
      window.open(url, '_blank', 'noopener');
    },
    extractError(e) {
      return e?.response?.data?.detail || e?.response?.data?.message || e?.message;
    },
    showSnackbar(text, color = 'success') {
      this.snackbar = { visible: true, text, color };
    },
  },
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
